import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import P, { P2 } from "../../components/Paragraph";
import Button from "../../components/Button";
import useLang from "../../hooks/useLang";
import {
  useGetFeatureModelQuery,
  useTrainFeatureModelMutation,
  useRollbackFeatureModelMutation,
} from "./featuresSlice";
import {
  TERMINAL_TRAINING_STATES,
  isOwnRunFollowed,
  modelWentLive,
  ownRunSeen,
  ownRunTimedOut,
  selectTrainPost,
  trainPostSent,
  trainPostSettled,
} from "./trainPostsSlice";
import { apiSlice } from "../api/apiSlice";
import { getErrorMessage } from "../../utils/errorUtils";
import styles from "./modelEvaluation.module.css";

const POLL_INTERVAL_MS = 5000;
const ACTIVE_TRAINING_STATES = ["queued", "running"];
// A run is followed for at most this long, whether the tab started it or not:
// the train Lambda's 900 s timeout plus queueing. An older queued or running
// job is stranded, and the curator trains again.
const TRAINING_TIMEOUT_MS = 20 * 60 * 1000;
// Training started while the tab had no model data (its model GET failed).
const MODEL_NOT_LOADED = Symbol("model not loaded");

// A new model is live: clear its own cache and the predictions it produces so
// the Model and Prognosis tabs reflect the new version.
const refreshCaches = (dispatch, featureId) =>
  dispatch(
    apiSlice.util.invalidateTags([
      { type: "FeatureModel", id: featureId },
      { type: "FeaturePredictions", id: featureId },
    ])
  );

const formatCount = (value, dash) => {
  if (dash) return "—";
  if (value == null) return "—";
  return value;
};

const EvaluationCell = ({ label, current, previous, hasPrevious, dash, lang }) => (
  <div className={styles.modelEvaluationCell}>
    <P2 style={{ color: "var(--color-base)", fontWeight: "600" }}>{label}</P2>
    <P>{formatCount(current, dash)}</P>
    {hasPrevious ? (
      <>
        <P2>{lang("model.previous_model")}</P2>
        <P style={{ color: "var(--color-meta)" }}>
          {formatCount(previous, dash)}
        </P>
      </>
    ) : null}
  </div>
);

const ModelEvaluation = () => {
  const lang = useLang();
  const dispatch = useDispatch();
  const store = useStore();
  const { featureId } = useParams();
  // A run whose 202 named no training_job_id (an API from before run ids) is
  // followed as before: by the active model's id, in this instance only.
  const [trainingFromModelId, setTrainingFromModelId] = useState(undefined);
  const timeoutRef = useRef(null);
  const followingRunWithoutId = trainingFromModelId !== undefined;
  // The run this tab started is followed by its id, kept in the store so that
  // a remount still follows it.
  const trainPost = useSelector((state) => selectTrainPost(state, featureId));
  const ownRun = trainPost.run;
  const followingOwnRun = isOwnRunFollowed(ownRun);

  // Set by the effect below from the live_evaluation pending count, so the tab
  // keeps polling while background inference is still in flight — not only
  // during an explicit retrain.
  const [livePollActive, setLivePollActive] = useState(false);
  // Set by an effect below while the tab follows a run it did not start, which
  // only the payload names.
  const [otherRunPollActive, setOtherRunPollActive] = useState(false);

  const { data, isLoading, status, requestId, refetch } =
    useGetFeatureModelQuery(
      { id: featureId },
      {
        skip: featureId == null,
        pollingInterval:
          followingOwnRun ||
          followingRunWithoutId ||
          otherRunPollActive ||
          livePollActive
            ? POLL_INTERVAL_MS
            : 0,
        refetchOnMountOrArgChange: true,
      }
    );
  // The model GET cached or in flight when the tab opened: its payload is not
  // the tab's own.
  const [requestIdAtMount] = useState(requestId);
  // RTK runs one model GET at a time, so a fulfilled state's request is the
  // shown payload's; keep it while a later request is pending or fails.
  const payloadRequestIdRef = useRef(null);
  if (status === "fulfilled") payloadRequestIdRef.current = requestId;
  const [trainModel, trainState] = useTrainFeatureModelMutation();
  const [rollbackModel, rollbackState] = useRollbackFeatureModelMutation();

  const current = data?.current ?? null;
  const previous = data?.previous ?? null;
  const currentId = current?.id ?? null;
  // `undefined` while the API does not send the field yet; the tab then
  // behaves as it did before the field existed.
  const lastTraining = data?.last_training;
  const lastTrainingId = lastTraining?.id;
  const lastTrainingState = lastTraining?.state;

  // A run the tab did not start, followed as its own would be while it is
  // queued or running and young.
  const followingOtherRun =
    !followingOwnRun &&
    !followingRunWithoutId &&
    lastTraining != null &&
    lastTrainingId !== ownRun?.jobId &&
    ACTIVE_TRAINING_STATES.includes(lastTrainingState) &&
    Date.now() - Date.parse(lastTraining.queued_at) < TRAINING_TIMEOUT_MS;
  const isTraining =
    followingOwnRun || followingRunWithoutId || followingOtherRun;

  // Poll while live_evaluation.pending > 0 so the pending/errored line and live
  // counts settle on their own. `errored` is intentionally not a trigger — it
  // is terminal and would poll forever.
  const livePending = current?.live_evaluation?.pending ?? 0;
  useEffect(() => {
    setLivePollActive(livePending > 0);
  }, [livePending]);

  // The tab's own run ends when the model GET shows its id finished; a
  // last_training with another id is another run.
  useEffect(() => {
    if (ownRun == null || lastTrainingId !== ownRun.jobId) return;
    if (TERMINAL_TRAINING_STATES.includes(ownRun.state)) return;
    if (lastTrainingState === ownRun.state) return;
    dispatch(
      ownRunSeen({ featureId, jobId: ownRun.jobId, state: lastTrainingState })
    );
    if (lastTrainingState === "succeeded") refreshCaches(dispatch, featureId);
  }, [ownRun, lastTrainingId, lastTrainingState, featureId, dispatch]);

  // A run the tab did not start is followed until it ends; if it succeeded,
  // the caches are refreshed as for the tab's own.
  const followedOtherRunRef = useRef(false);
  useEffect(() => {
    setOtherRunPollActive(followingOtherRun);
    const wasFollowing = followedOtherRunRef.current;
    followedOtherRunRef.current = followingOtherRun;
    if (
      wasFollowing &&
      !followingOtherRun &&
      lastTrainingState === "succeeded"
    ) {
      refreshCaches(dispatch, featureId);
    }
  }, [followingOtherRun, lastTrainingState, featureId, dispatch]);

  // Prognozė checks its predictions against the active model the tab last
  // saw, however that model went live.
  const liveModelId = trainPost.liveModelId;
  useEffect(() => {
    if (currentId != null && currentId !== liveModelId) {
      dispatch(modelWentLive({ featureId, modelId: currentId }));
    }
  }, [currentId, liveModelId, featureId, dispatch]);

  // Stop following a run without an id once the active model has flipped, or
  // the safety timeout has fired (training stuck somewhere we can't recover
  // from).
  const hasData = data !== undefined;
  useEffect(() => {
    if (!followingRunWithoutId) return;
    if (trainingFromModelId === MODEL_NOT_LOADED) {
      // A model the tab never loaded is not a model id: the first one a
      // successful poll returns is the one to compare against.
      if (hasData) setTrainingFromModelId(currentId);
      return;
    }
    if (currentId === trainingFromModelId) return;
    setTrainingFromModelId(undefined);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (featureId != null) refreshCaches(dispatch, featureId);
  }, [
    currentId,
    hasData,
    trainingFromModelId,
    followingRunWithoutId,
    featureId,
    dispatch,
  ]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  // A GET already out when the tab opened absorbs its refetch-on-mount; once
  // it lands, fetch again so the tab has a payload of its own.
  useEffect(() => {
    if (status === "fulfilled" && requestId === requestIdAtMount) refetch();
  }, [status, requestId, requestIdAtMount, refetch]);

  const handleRetrain = async () => {
    // Read from the store, not this render: a second click can land before
    // the render that disables the button.
    if (isTraining || selectTrainPost(store.getState(), featureId).out > 0) {
      return;
    }
    const before = hasData ? currentId : MODEL_NOT_LOADED;
    dispatch(trainPostSent(featureId));
    try {
      const answer = await trainModel({ params: { featureId } }).unwrap();
      const jobId = answer?.training_job_id;
      dispatch(trainPostSettled({ featureId, jobId }));
      if (jobId != null) {
        // Not cleared on unmount: the run outlives this instance, and a
        // remount follows it until the backstop fires.
        setTimeout(
          () => dispatch(ownRunTimedOut({ featureId, jobId })),
          TRAINING_TIMEOUT_MS
        );
        return;
      }
      setTrainingFromModelId(before);
      timeoutRef.current = setTimeout(() => {
        setTrainingFromModelId(undefined);
        timeoutRef.current = null;
      }, TRAINING_TIMEOUT_MS);
    } catch (err) {
      dispatch(trainPostSettled({ featureId }));
      // Error surfaces via trainState.error below.
    }
  };

  const handleRollback = async () => {
    if (rollbackState.isLoading || isTraining) return;
    try {
      await rollbackModel({ params: { featureId } }).unwrap();
    } catch (err) {
      // Error surfaces via rollbackState.error below.
    }
  };

  if (isLoading && !data) {
    return <P>{lang("model.loading")}</P>;
  }

  const hasCurrent = current !== null;
  const hasPrevious = previous !== null;
  const isAbnormal = !!current?.is_abnormal_training_set;
  const isStale = !!current?.is_stale;
  const trainBusy = isTraining || trainPost.out > 0;
  // A 409 means the feature lacks a positive or a negative example; the API's
  // own text for it is English, so the tab says it in Lithuanian instead.
  const trainError =
    trainState.error?.status === 409
      ? lang("model.needs_positive_and_negative")
      : getErrorMessage(trainState.error);
  const rollbackError = getErrorMessage(rollbackState.error);
  // A failure is shown only from a payload the tab fetched itself, never while
  // a train POST for the feature is out or a run is followed, and, once the
  // tab has started a run, only after it has seen that run on the wire: until
  // then a failure may be an older run's, which its own has replaced.
  const payloadRequestId = payloadRequestIdRef.current;
  const trainingFailed =
    !isTraining &&
    trainPost.out === 0 &&
    payloadRequestId != null &&
    payloadRequestId !== requestIdAtMount &&
    lastTrainingState === "failed" &&
    (ownRun == null || ownRun.state != null);

  return (
    <div
      style={{
        display: "flex",
        gap: "var(--gap)",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <div style={{ height: "var(--size-4)" }} />
      <div
        style={{
          display: "flex",
          gap: "var(--gap)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {hasCurrent ? (
          <P>{`${lang("captions.version")}: ${
            current.version != null ? `v${current.version}` : "—"
          }`}</P>
        ) : (
          <P>{lang("model.not_trained_yet")}</P>
        )}
        {isStale ? (
          <span className={styles.staleBadge}>
            {lang("model.outdated_badge")}
          </span>
        ) : null}
        {isAbnormal ? (
          <span className={styles.abnormalBadge}>
            {lang("model.abnormal_training_set")}
          </span>
        ) : null}
      </div>

      <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
        <Button
          mutation="features.id.model.train.POST"
          onClick={handleRetrain}
          disabled={trainBusy}
          loader={trainBusy}
        >
          {isTraining ? lang("model.updating") : lang("model.update_model")}
        </Button>
        {hasPrevious ? (
          <Button
            mutation="features.id.model.rollback.POST"
            onClick={handleRollback}
            disabled={rollbackState.isLoading || isTraining}
            loader={rollbackState.isLoading}
          >
            {lang("model.fall_back_to_previous_version")}
          </Button>
        ) : null}
      </div>

      {trainingFailed ? (
        <div className={styles.trainingFailed}>
          <P2 className={styles.error}>{lang("model.training_failed")}</P2>
          {lastTraining.error ? (
            <details>
              <summary className={styles.errorSummary}>
                {lang("model.technical_details")}
              </summary>
              <pre className={styles.errorText}>{lastTraining.error}</pre>
            </details>
          ) : null}
        </div>
      ) : null}
      {trainError ? <P2 className={styles.error}>{trainError}</P2> : null}
      {rollbackError ? (
        <P2 className={styles.error}>{rollbackError}</P2>
      ) : null}

      {hasCurrent ? (
        <>
          <div style={{ height: "var(--size-4)" }} />
          <P>{lang("model.evaluation_at_training")}</P>
          {hasPrevious ? (
            <P2>
              {`${lang("model.evaluation_compared_to_previous")} (${
                previous.version != null ? `v${previous.version}` : "—"
              })`}
            </P2>
          ) : null}
          <div
            className={classNames(styles.modelEvaluationTable, {
              [styles.deemphasized]: isAbnormal,
            })}
          >
            <div className={styles.modelEvaluationRow}>
              <EvaluationCell
                label={lang("model.true_positive")}
                current={current.evaluation?.true_positive}
                previous={previous?.evaluation?.true_positive}
                hasPrevious={hasPrevious}
                dash={isAbnormal}
                lang={lang}
              />
              <EvaluationCell
                label={lang("model.false_negative")}
                current={current.evaluation?.false_negative}
                previous={previous?.evaluation?.false_negative}
                hasPrevious={hasPrevious}
                dash={isAbnormal}
                lang={lang}
              />
            </div>
            <div className={styles.modelEvaluationRow}>
              <EvaluationCell
                label={lang("model.false_positive")}
                current={current.evaluation?.false_positive}
                previous={previous?.evaluation?.false_positive}
                hasPrevious={hasPrevious}
                dash={isAbnormal}
                lang={lang}
              />
              <EvaluationCell
                label={lang("model.true_negative")}
                current={current.evaluation?.true_negative}
                previous={previous?.evaluation?.true_negative}
                hasPrevious={hasPrevious}
                dash={isAbnormal}
                lang={lang}
              />
            </div>
          </div>
          {current.live_evaluation ? (
            <>
              <div style={{ height: "var(--size-4)" }} />
              <P>{lang("model.evaluation_live")}</P>
              <div className={styles.modelEvaluationTable}>
                <div className={styles.modelEvaluationRow}>
                  <EvaluationCell
                    label={lang("model.true_positive")}
                    current={current.live_evaluation.true_positive}
                    hasPrevious={false}
                    lang={lang}
                  />
                  <EvaluationCell
                    label={lang("model.false_negative")}
                    current={current.live_evaluation.false_negative}
                    hasPrevious={false}
                    lang={lang}
                  />
                </div>
                <div className={styles.modelEvaluationRow}>
                  <EvaluationCell
                    label={lang("model.false_positive")}
                    current={current.live_evaluation.false_positive}
                    hasPrevious={false}
                    lang={lang}
                  />
                  <EvaluationCell
                    label={lang("model.true_negative")}
                    current={current.live_evaluation.true_negative}
                    hasPrevious={false}
                    lang={lang}
                  />
                </div>
              </div>
              <P2 style={{ color: "var(--color-meta)" }}>
                {`${lang("prognosis.pending")}: ${
                  current.live_evaluation.pending ?? 0
                } · ${lang("prognosis.errored")}: ${
                  current.live_evaluation.errored ?? 0
                }`}
              </P2>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ModelEvaluation;
